<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhitelabAPI from 'dashboard/api/integrations/whitelab';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

// No close event: unlike LiveChatFormsPanel this has no in-body back control, so
// the shared header Back button is the single way out. Declaring an emit nothing
// fires would just be dead wiring.

const { t } = useI18n();

const context = ref(null);
// Who else this chat account has attested as, and how many other accounts this
// player has written from. Comes with the context payload, dropped with it.
const identityHistory = ref(null);
// Which data groups the server let through for this reader. Kept apart from
// the data itself because a missing key means two different things — "your
// role may not see this" and "there is nothing here" — and on a money surface
// the agent must never mistake one for the other.
const visibleGroups = ref([]);
const GROUPS = ['basic', 'finance', 'risk', 'contact', 'technical'];
const canSee = group => visibleGroups.value.includes(group);
const hiddenGroups = computed(() =>
  GROUPS.filter(group => !visibleGroups.value.includes(group))
);
const loading = ref(false);
const errorCode = ref('');
// The status the platform actually answered with. Without it an unhandled code
// is indistinguishable from an outage in the message shown to the agent.
const upstreamStatus = ref(null);
const phone = ref('');
const phoneLoading = ref(false);
const phoneError = ref('');
const loginIp = ref('');
const loginIpAt = ref('');
const ipLoading = ref(false);
const ipError = ref('');
const ipRevealed = ref(false);

const player = computed(() => context.value?.player || {});
const wallet = computed(() => context.value?.wallet || {});
const funding = computed(() => context.value?.funding || {});
const derived = computed(() => context.value?.derived || {});
const access = computed(() => context.value?.access || {});

const risk = computed(() => context.value?.risk || {});

// Nothing shows until an admin has actually decided. A level sitting in the
// payload with decided:false is a default, not a judgement, and dressing it up
// as one would have agents treating untouched accounts as cleared ones.
const riskDecided = computed(() => risk.value.decided === true);

const riskLevel = computed(() =>
  riskDecided.value ? String(risk.value.level || '').toUpperCase() : ''
);

// NORMAL is the absence of a badge, not a badge saying "normal": the panel is
// read at a glance and a chip on every player teaches agents to ignore chips.
const riskBadge = computed(() => {
  if (!riskLevel.value || riskLevel.value === 'NORMAL') return null;
  if (riskLevel.value === 'WATCH') {
    return {
      label: t('CONVERSATION_SIDEBAR.WHITELAB.RISK_WATCH'),
      class: 'bg-n-amber-3 text-n-amber-11',
    };
  }
  if (riskLevel.value === 'HIGH') {
    return {
      label: t('CONVERSATION_SIDEBAR.WHITELAB.RISK_HIGH'),
      class: 'bg-n-ruby-3 text-n-ruby-11',
    };
  }
  // A level this build has never heard of. Shown plainly rather than hidden:
  // the platform can add one without telling us, and an unreadable risk is
  // still a risk — swallowing it would be the worse failure of the two.
  return { label: riskLevel.value, class: 'bg-n-slate-3 text-n-slate-11' };
});

// Written by a platform admin for agents to say out loud, so it is shown
// whenever it exists — but only once someone has decided, since an
// undecided account should carry no instruction at all.
const riskNote = computed(() =>
  riskDecided.value ? String(risk.value.supportNote || '').trim() : ''
);

// Only these two carry a warning strip. Any other value Whitelab may add later
// falls through as a normal ACTIVE-looking record rather than rendering a
// missing translation key at an agent.
const BLOCKING_STATUSES = ['SUSPENDED', 'BANNED'];

const isBlocked = computed(() =>
  BLOCKING_STATUSES.includes(String(player.value.status || ''))
);

const blockedLabel = computed(() =>
  isBlocked.value
    ? t(`CONVERSATION_SIDEBAR.WHITELAB.STATUS.${player.value.status}`)
    : ''
);
// Spelled out rather than built from the status string: an unknown status must
// still read as something, and a key assembled at runtime is invisible to both
// grep and the linter.
const playerStatusLabel = computed(() => {
  const raw = String(player.value.status || '');
  if (raw === 'ACTIVE') return t('CONVERSATION_SIDEBAR.WHITELAB.STATUS.ACTIVE');
  if (raw === 'SUSPENDED')
    return t('CONVERSATION_SIDEBAR.WHITELAB.STATUS.SUSPENDED');
  if (raw === 'BANNED') return t('CONVERSATION_SIDEBAR.WHITELAB.STATUS.BANNED');
  return raw;
});

const previousIdentities = computed(
  () => identityHistory.value?.previous || []
);
const previousIdentitiesOverflow = computed(() =>
  Math.max(
    0,
    (identityHistory.value?.previous_total || 0) -
      previousIdentities.value.length
  )
);
const otherSourceCount = computed(
  () => identityHistory.value?.other_sources || 0
);
const identityLabel = row => row.username || row.identifier || '';

const isBalanceStale = computed(() => wallet.value.balanceStale === true);
// "0.00" is a string and truthy, so a plain check would show an empty bonus
// wallet on every player who has never had one.
const hasUnclaimedBalance = computed(
  () => Number(wallet.value.unclaimedBalance || 0) > 0
);
const pendingWithdrawCount = computed(
  () => Number(funding.value.pendingWithdrawCount || 0) || 0
);

// Whitelab sends money as a decimal string in baht, deliberately — a float would
// round money wrong. Keep it a string all the way to Intl so nothing ever parses
// it into a Number for display.
const formatMoney = value => {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number.parseFloat(value);
  if (Number.isNaN(amount)) return String(value);
  return new Intl.NumberFormat('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Every date here is rendered in the zone the payload names, never the reader's
// browser. An agent reads these figures back to a player while the platform's
// own back office shows the same numbers, and the two disagreeing by an hour is
// not something either of them can spot. Blank falls through to the browser, so
// an account that never set one is no worse off than before.
const displayTimezone = computed(() => derived.value.timezone || undefined);

const dateTimeParts = {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
};

const formatInZone = (value, parts, empty) => {
  if (!value) return empty;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return empty;
  try {
    return new Intl.DateTimeFormat(undefined, {
      ...parts,
      timeZone: displayTimezone.value,
    }).format(parsed);
  } catch (error) {
    // An unrecognised zone would otherwise throw and blank the whole panel.
    return new Intl.DateTimeFormat(undefined, parts).format(parsed);
  }
};

const formatDateTime = value => formatInZone(value, dateTimeParts, '—');

// Carries the date as well as the clock. Showing "02:00 AM" for a balance that
// was read the day before yesterday invites an agent to quote a two-day-old
// figure as current, which on a money surface is the whole risk.
const formatTime = value => formatInZone(value, dateTimeParts, '');

const errorMessage = computed(() => {
  // A code with no message of its own used to fall through to "the platform is
  // temporarily unavailable", which reads as an outage. When the platform
  // answered perfectly well with a status this build does not handle, that
  // sends whoever is debugging to the wrong place — say the status instead.
  if (errorCode.value === 'unexpected_status') {
    return t('CONVERSATION_SIDEBAR.WHITELAB.ERRORS.UNEXPECTED_STATUS', {
      status: upstreamStatus.value ?? '?',
    });
  }

  const key =
    {
      not_found: 'NOT_FOUND',
      not_linked: 'NOT_LINKED',
      not_configured: 'NOT_CONFIGURED',
      identity_unverified: 'IDENTITY_UNVERIFIED',
      not_permitted: 'NOT_PERMITTED',
      unauthorized: 'UNAUTHORIZED',
      rate_limited: 'RATE_LIMITED',
      audit_unavailable: 'AUDIT_UNAVAILABLE',
      timeout: 'UNREACHABLE',
      connection_error: 'UNREACHABLE',
      bad_request: 'BAD_REQUEST',
      bad_response: 'BAD_RESPONSE',
    }[errorCode.value] || 'UPSTREAM_ERROR';
  return t(`CONVERSATION_SIDEBAR.WHITELAB.ERRORS.${key}`);
});

const readErrorCode = error =>
  error?.response?.data?.error || error?.message || 'upstream_error';

const readUpstreamStatus = error =>
  error?.response?.data?.upstream_status ?? null;

const fetchContext = async ({ refresh = false } = {}) => {
  loading.value = true;
  errorCode.value = '';
  upstreamStatus.value = null;
  // Anything already on screen is dropped before the request, not after. These
  // are money figures: showing the previous player's balance, or a stale one,
  // while a refresh is in flight is worse than showing nothing.
  context.value = null;
  identityHistory.value = null;
  visibleGroups.value = [];
  phone.value = '';
  phoneError.value = '';
  // A revealed IP belongs to the player it was revealed for. Carrying one
  // into the next conversation would put one player's location beside
  // another's name, which is worse than showing nothing.
  loginIp.value = '';
  loginIpAt.value = '';
  ipError.value = '';
  ipRevealed.value = false;
  try {
    const { data } = await WhitelabAPI.getPlayerContext(props.conversationId, {
      refresh,
    });
    context.value = data.data;
    identityHistory.value = data.identity_history || null;
    visibleGroups.value = Array.isArray(data.visible_groups)
      ? data.visible_groups
      : GROUPS;
  } catch (error) {
    errorCode.value = readErrorCode(error);
    upstreamStatus.value = readUpstreamStatus(error);
  } finally {
    loading.value = false;
  }
};

// The IP never arrives with the panel payload — it is stripped server-side and
// kept out of the cache — so revealing it is a fresh, audited read rather than
// unhiding something already in the browser.
const revealIp = async () => {
  ipLoading.value = true;
  ipError.value = '';
  try {
    const { data } = await WhitelabAPI.getPlayerIp(props.conversationId);
    // Absent is not the same as empty here: a player the platform has no IP for
    // yet must read as "no data", never as a blank that looks like a failure.
    loginIp.value = data.ip || '';
    loginIpAt.value = data.recorded_at || '';
    ipRevealed.value = true;
  } catch (error) {
    ipError.value = readErrorCode(error);
  } finally {
    ipLoading.value = false;
  }
};

const revealPhone = async () => {
  phoneLoading.value = true;
  phoneError.value = '';
  try {
    const { data } = await WhitelabAPI.getPlayerPhone(props.conversationId);
    phone.value = data.phone || '';
  } catch (error) {
    phoneError.value = readErrorCode(error);
  } finally {
    phoneLoading.value = false;
  }
};

watch(
  () => props.conversationId,
  () => fetchContext(),
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-3 px-4 py-3 text-n-slate-12">
    <div v-if="loading" class="flex items-center justify-center py-10">
      <Spinner :size="28" class="text-n-slate-10" />
    </div>

    <div v-else-if="errorCode" class="flex flex-col items-start gap-3 py-6">
      <p class="text-sm text-n-slate-11">{{ errorMessage }}</p>
      <NextButton
        sm
        faded
        slate
        :label="$t('CONVERSATION_SIDEBAR.WHITELAB.RETRY')"
        @click="fetchContext({ refresh: true })"
      />
    </div>

    <template v-else-if="context">
      <div class="flex items-center justify-between gap-2">
        <span class="flex items-baseline min-w-0 gap-2">
          <span class="text-sm font-medium truncate">
            {{
              player.username || $t('CONVERSATION_SIDEBAR.WHITELAB.NO_USERNAME')
            }}
          </span>
          <!-- Operator-configured, and absent until they set one. The platform
               behind this is white-labelled, so no name may come from code. -->
          <img
            v-if="derived.brandLogoUrl"
            :src="derived.brandLogoUrl"
            alt=""
            class="object-contain w-4 h-4 rounded shrink-0"
          />
          <span
            v-if="derived.brandName"
            class="text-xs truncate shrink-0 text-n-slate-10"
          >
            {{ derived.brandName }}
          </span>
          <!-- Beside the name because it changes how everything below it is
               read. shrink-0 so the username gives up room first: a truncated
               name is recoverable, a truncated risk level is not. -->
          <span
            v-if="riskBadge"
            class="px-1.5 py-0.5 text-xs font-medium rounded-full shrink-0"
            :class="riskBadge.class"
          >
            {{ riskBadge.label }}
          </span>
        </span>
        <NextButton
          v-tooltip.top-end="$t('CONVERSATION_SIDEBAR.WHITELAB.REFRESH')"
          icon="i-ph-arrows-clockwise-bold"
          slate
          faded
          xs
          @click="fetchContext({ refresh: true })"
        />
      </div>

      <!-- Named, not silent. Rows for these groups simply do not render because
           the server never sent the data, which is indistinguishable from a
           player who has none — so the reader is told which groups their role
           withholds. -->
      <div
        v-if="hiddenGroups.length"
        class="flex flex-col gap-0.5 px-3 py-2 text-xs rounded-lg bg-n-slate-3 text-n-slate-11"
      >
        <span class="font-medium">
          {{ $t('CONVERSATION_SIDEBAR.WHITELAB.RESTRICTED_TITLE') }}
        </span>
        <span>
          {{
            hiddenGroups
              .map(group =>
                $t(`CONVERSATION_SIDEBAR.WHITELAB.GROUP_${group.toUpperCase()}`)
              )
              .join(' · ')
          }}
        </span>
      </div>

      <div
        v-if="isBlocked"
        class="px-3 py-2 text-sm font-medium rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{ blockedLabel }}
      </div>

      <!-- Composed by a platform admin for agents to act on, and safe to say to
           the customer. Interpolated, never v-html: this is text from another
           company's admin console arriving in our agents' browsers. -->
      <div
        v-if="riskNote"
        class="flex flex-col gap-1 px-3 py-2 text-xs rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        <span class="font-medium">
          {{ $t('CONVERSATION_SIDEBAR.WHITELAB.RISK_NOTE_TITLE') }}
          <template v-if="risk.categoryLabel">
            · {{ risk.categoryLabel }}
          </template>
        </span>
        <span class="break-words whitespace-pre-line">{{ riskNote }}</span>
        <!-- A note with no date reads as one written today. These are set once
             and left, so the age of the instruction is part of the instruction. -->
        <span v-if="risk.setAt" class="opacity-80">
          {{
            $t('CONVERSATION_SIDEBAR.WHITELAB.RISK_NOTE_SET_AT', {
              date: formatDateTime(risk.setAt),
            })
          }}
        </span>
      </div>

      <!-- A warning strip, not a data row: an account that has been somebody
           else is the one thing here that changes how the agent reads
           everything under it. Names are shown plainly — the agent needs the
           signal before deciding whether to go and check in the back office,
           and opening this panel is already audited. -->
      <div
        v-if="previousIdentities.length || otherSourceCount"
        class="flex flex-col gap-1 px-3 py-2 text-xs rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        <template v-if="previousIdentities.length">
          <span class="font-medium">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.HISTORY.PREVIOUS_TITLE') }}
          </span>
          <span
            v-for="row in previousIdentities"
            :key="row.identifier + row.linked_at"
            class="tabular-nums"
          >
            {{ identityLabel(row) }} · {{ formatDateTime(row.linked_at) }}
          </span>
          <span v-if="previousIdentitiesOverflow > 0">
            {{
              $t('CONVERSATION_SIDEBAR.WHITELAB.HISTORY.MORE', {
                count: previousIdentitiesOverflow,
              })
            }}
          </span>
        </template>
        <span v-if="otherSourceCount">
          {{
            $t('CONVERSATION_SIDEBAR.WHITELAB.HISTORY.OTHER_SOURCES', {
              count: otherSourceCount,
            })
          }}
        </span>
      </div>

      <div v-if="canSee('finance')" class="flex flex-col gap-1 py-1">
        <span class="text-xs uppercase text-n-slate-10">
          {{ $t('CONVERSATION_SIDEBAR.WHITELAB.BALANCE') }}
        </span>
        <span class="text-xl font-semibold tabular-nums">
          {{ formatMoney(wallet.balance) }}
          <span class="text-sm font-normal text-n-slate-11">
            {{ wallet.currency }}
          </span>
        </span>
        <!-- The platform keeps bonus money in its own wallet. A player asking
             "why can I not withdraw all of it" is asking about this number, so
             leaving it out sent agents to the back office to find it. -->
        <span
          v-if="hasUnclaimedBalance"
          class="text-sm tabular-nums text-n-slate-11"
        >
          {{
            $t('CONVERSATION_SIDEBAR.WHITELAB.UNCLAIMED_BALANCE', {
              amount: formatMoney(wallet.unclaimedBalance),
              currency: wallet.currency,
            })
          }}
        </span>
        <!-- Always shown, never optional: Whitelab reads the wallet from their
             own cache, so an agent confirming a figure needs to know how old it
             is before they say it out loud. -->
        <span
          class="text-xs"
          :class="isBalanceStale ? 'text-n-amber-11' : 'text-n-slate-10'"
        >
          {{
            isBalanceStale
              ? $t('CONVERSATION_SIDEBAR.WHITELAB.BALANCE_STALE', {
                  time: formatTime(wallet.balanceAsOf || context.asOf),
                })
              : $t('CONVERSATION_SIDEBAR.WHITELAB.BALANCE_AS_OF', {
                  time: formatTime(wallet.balanceAsOf || context.asOf),
                })
          }}
        </span>
      </div>

      <dl class="flex flex-col gap-2 text-sm">
        <div
          v-if="pendingWithdrawCount > 0"
          class="flex items-baseline justify-between gap-3 px-3 py-2 rounded-lg bg-n-amber-3"
        >
          <dt class="text-n-amber-11">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.PENDING_WITHDRAW') }}
          </dt>
          <dd class="font-medium text-right tabular-nums text-n-amber-11">
            {{
              $t('CONVERSATION_SIDEBAR.WHITELAB.PENDING_WITHDRAW_VALUE', {
                count: pendingWithdrawCount,
                amount: formatMoney(funding.pendingWithdrawTotal),
              })
            }}
          </dd>
        </div>

        <div class="flex items-baseline justify-between gap-3">
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.LAST_DEPOSIT') }}
          </dt>
          <dd class="text-right tabular-nums">
            <!-- Amount and date travel together: a figure with no date invites an
            agent to quote a months-old deposit as the latest one. A player who
            has never deposited reads as one placeholder, not two. -->
            <span v-if="!canSee('finance')" class="text-n-slate-10">
              {{ $t('CONVERSATION_SIDEBAR.WHITELAB.NO_PERMISSION') }}
            </span>
            <span
              v-else-if="funding.lastDepositAt || funding.lastDepositAmount"
            >
              {{
                $t('CONVERSATION_SIDEBAR.WHITELAB.AMOUNT_ON_DATE', {
                  amount: formatMoney(funding.lastDepositAmount),
                  date: formatDateTime(funding.lastDepositAt),
                })
              }}
            </span>
            <span v-else class="text-n-slate-10">
              {{ $t('CONVERSATION_SIDEBAR.WHITELAB.NO_DATA') }}
            </span>
          </dd>
        </div>

        <div
          v-if="funding.depositCount"
          class="flex items-baseline justify-between gap-3"
        >
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.DEPOSITS') }}
          </dt>
          <dd class="text-right tabular-nums">
            {{
              $t('CONVERSATION_SIDEBAR.WHITELAB.COUNT_AND_TOTAL', {
                count: funding.depositCount,
                amount: formatMoney(funding.depositTotal),
              })
            }}
          </dd>
        </div>

        <div
          v-if="funding.withdrawCount"
          class="flex items-baseline justify-between gap-3"
        >
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.WITHDRAWALS') }}
          </dt>
          <dd class="text-right tabular-nums">
            {{
              $t('CONVERSATION_SIDEBAR.WHITELAB.COUNT_AND_TOTAL', {
                count: funding.withdrawCount,
                amount: formatMoney(funding.withdrawTotal),
              })
            }}
          </dd>
        </div>

        <div
          v-if="derived.vipLabel"
          class="flex items-baseline justify-between gap-3"
        >
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.VIP') }}
          </dt>
          <dd class="text-right">{{ derived.vipLabel }}</dd>
        </div>

        <div
          v-if="derived.groups && derived.groups.length"
          class="flex items-baseline justify-between gap-3"
        >
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.GROUPS') }}
          </dt>
          <dd class="flex flex-wrap justify-end gap-1">
            <!-- One colour for every group. Whitelab's group names are created by
                 their operators and have no fixed vocabulary, so mapping a colour
                 to a name would break the day someone adds a group. The only
                 distinction is the operator-configured highlight list. -->
            <span
              v-for="group in derived.groups"
              :key="group"
              class="px-2 py-0.5 text-xs rounded-md"
              :class="
                (derived.highlightedGroups || []).includes(group)
                  ? 'bg-n-amber-3 text-n-amber-11 font-medium'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
            >
              {{ group }}
            </span>
          </dd>
        </div>

        <div class="flex items-baseline justify-between gap-3">
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.PHONE') }}
          </dt>
          <dd class="text-right">
            <span v-if="!canSee('contact')" class="text-n-slate-10">
              {{ $t('CONVERSATION_SIDEBAR.WHITELAB.NO_PERMISSION') }}
            </span>
            <span v-else-if="phone" class="tabular-nums">{{ phone }}</span>
            <span v-else-if="phoneError" class="text-xs text-n-ruby-11">
              {{ $t('CONVERSATION_SIDEBAR.WHITELAB.PHONE_FAILED') }}
            </span>
            <NextButton
              v-else
              xs
              faded
              slate
              :is-loading="phoneLoading"
              :label="$t('CONVERSATION_SIDEBAR.WHITELAB.REVEAL_PHONE')"
              @click="revealPhone"
            />
          </dd>
        </div>
        <div
          v-if="player.emailMasked"
          class="flex items-baseline justify-between gap-3"
        >
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.EMAIL') }}
          </dt>
          <dd class="flex items-center justify-end gap-1.5 text-right">
            <span class="truncate">{{ player.emailMasked }}</span>
            <span
              v-if="player.emailVerified"
              v-tooltip.top-end="$t('CONVERSATION_SIDEBAR.WHITELAB.VERIFIED')"
              class="i-ph-seal-check-fill text-n-teal-11 shrink-0"
            />
          </dd>
        </div>

        <div
          v-if="player.verificationLevel"
          class="flex items-baseline justify-between gap-3"
        >
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.VERIFICATION') }}
          </dt>
          <dd class="text-right">{{ player.verificationLevel }}</dd>
        </div>

        <div
          v-if="player.status && !isBlocked"
          class="flex items-baseline justify-between gap-3"
        >
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.STATUS_LABEL') }}
          </dt>
          <dd class="text-right">{{ playerStatusLabel }}</dd>
        </div>

        <div
          v-if="player.lastLoginAt"
          class="flex items-baseline justify-between gap-3"
        >
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.LAST_LOGIN') }}
          </dt>
          <dd class="text-right tabular-nums">
            {{ formatDateTime(player.lastLoginAt) }}
          </dd>
        </div>

        <div class="flex items-baseline justify-between gap-3">
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.LOGIN_IP') }}
          </dt>
          <dd class="text-right">
            <span v-if="!canSee('technical')" class="text-n-slate-10">
              {{ $t('CONVERSATION_SIDEBAR.WHITELAB.NO_PERMISSION') }}
            </span>
            <span v-else-if="loginIp" class="tabular-nums">
              {{
                $t('CONVERSATION_SIDEBAR.WHITELAB.IP_ON_DATE', {
                  ip: loginIp,
                  date: formatDateTime(loginIpAt),
                })
              }}
            </span>
            <span v-else-if="ipRevealed" class="text-n-slate-10">
              {{ $t('CONVERSATION_SIDEBAR.WHITELAB.NO_DATA') }}
            </span>
            <span v-else-if="ipError" class="text-xs text-n-ruby-11">
              {{ $t('CONVERSATION_SIDEBAR.WHITELAB.IP_FAILED') }}
            </span>
            <NextButton
              v-else
              xs
              faded
              slate
              :is-loading="ipLoading"
              :label="$t('CONVERSATION_SIDEBAR.WHITELAB.REVEAL_IP')"
              @click="revealIp"
            />
          </dd>
        </div>

        <div
          v-if="
            access.distinctIpCount !== null &&
            access.distinctIpCount !== undefined
          "
          class="flex items-baseline justify-between gap-3"
        >
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.DISTINCT_IPS') }}
          </dt>
          <dd
            v-tooltip.top-end="
              $t('CONVERSATION_SIDEBAR.WHITELAB.DISTINCT_IPS_HINT')
            "
            class="text-right tabular-nums"
          >
            {{
              $t('CONVERSATION_SIDEBAR.WHITELAB.DISTINCT_IPS_VALUE', {
                count: access.distinctIpCount,
                days: access.distinctIpWindowDays,
              })
            }}
          </dd>
        </div>

        <div
          v-if="player.registeredAt"
          class="flex items-baseline justify-between gap-3"
        >
          <dt class="text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.WHITELAB.REGISTERED') }}
          </dt>
          <dd class="text-right tabular-nums">
            {{ formatDateTime(player.registeredAt) }}
          </dd>
        </div>
      </dl>

      <!-- Named once at the foot rather than repeated on every row. The platform
           serves more than one country, so "02:00" on its own does not say which
           country's two o'clock it is. -->
      <p v-if="displayTimezone" class="text-xs text-n-slate-10">
        {{
          $t('CONVERSATION_SIDEBAR.WHITELAB.TIMEZONE_NOTE', {
            zone: displayTimezone,
          })
        }}
      </p>

      <a
        v-if="derived.boProfileUrl"
        :href="derived.boProfileUrl"
        target="_blank"
        rel="noopener nofollow noreferrer"
        class="inline-flex items-center gap-1 text-sm text-n-blue-11 hover:underline"
      >
        {{ $t('CONVERSATION_SIDEBAR.WHITELAB.OPEN_IN_BO') }}
        <span class="i-lucide-external-link text-xs" />
      </a>
    </template>
  </div>
</template>
